import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { VatAmountCalculator } from '@zaoblako/shared/common/classes/vat.amount.calculator';
import { CurrencyFindInterface } from '@zaoblako/shared/modules/catalog/dtos/currency/currency-find.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { BankDetailsFindInterface } from '@zaoblako/shared/modules/client/dtos/bank.details/bank-details-find.dto';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import {
  DocumentCreateDto,
  type DocumentCreateInterface
} from '@zaoblako/shared/modules/document/dtos/document/document-create.dto';
import type { DocumentFindInterface } from '@zaoblako/shared/modules/document/dtos/document/document-find.dto';
import { DocumentCalculationMethodEnum } from '@zaoblako/shared/modules/document/enums/document.calculation.method.enum';
import { DocumentKeyEnum } from '@zaoblako/shared/modules/document/enums/document.key.enum';
import type { NdsFindInterface } from '@zaoblako/shared/modules/finance/dtos/nds-find.dto';
import { DocumentRegistryService } from '@zaoblako/web/core/services/document/document-registry.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { DocumentHistoryComponent } from '@zaoblako/web/modules/document/dumbs/document-form-sidebar/components/document-history/document-history.component';
import { DocumentSplitWidgetComponent } from '@zaoblako/web/modules/document/dumbs/document-form-sidebar/components/document-split-widget/document-split-widget.component';
import { TypeFormEnum } from '@zaoblako/web/shared/components/form-sidebar/enums/type.form.enum';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { GoBackButtonComponent } from '@zaoblako/web/shared/components/go-back-button/go-back-button.component';
import { BankDetailsAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/bank-details-auto-complete/bank-details-auto-complete.component';
import { ContragentAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/contragent-auto-complete/contragent-auto-complete.component';
import { DealAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/deal-auto-complete/deal-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { ContractSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/contract-single-select/contract-single-select.component';
import { CostCenterSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/cost-center-single-select/cost-center-single-select.component';
import { CurrencySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/currency-single-select/currency-single-select.component';
import { DirectionSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/direction-single-select/direction-single-select.component';
import { DocumentTypeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/document-type-single-select/document-type-single-select.component';
import { FilialSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/filial-single-select/filial-single-select.component';
import { OperationTypeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/operation-type-single-select/operation-type-single-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { UnitSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/unit-single-select/unit-single-select.component';
import { EditorComponent } from '@zaoblako/web/shared/dumbs/editor/editor.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import * as moment from 'moment-timezone';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { MessagesModule } from 'primeng/messages';
import { Subscription } from 'rxjs';

interface RegistryFormDto extends DocumentCreateInterface {
  code?: string;
}

@Component({
  standalone: true,
  selector: 'crm-document-form',
  templateUrl: './document-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    DatepickerComponent,
    JoinPipe,
    InputNumberModule,
    FormsModule,
    SingleSelectComponent,
    ContragentAutoCompleteComponent,
    DirectionSingleSelectComponent,
    EditorComponent,
    CheckboxModule,
    ButtonModule,
    GoBackButtonComponent,
    RouterModule,
    DocumentHistoryComponent,
    BankDetailsAutoCompleteComponent,
    DealAutoCompleteComponent,
    DocumentSplitWidgetComponent,
    UnitSingleSelectComponent,
    OperationTypeSingleSelectComponent,
    FilialSingleSelectComponent,
    DocumentTypeSingleSelectComponent,
    CostCenterSingleSelectComponent,
    MessagesModule,
    CurrencySingleSelectComponent,
    ContractSingleSelectComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentFormComponent extends BaseFormComponent<DocumentCreateInterface> implements OnInit, OnDestroy {
  @Input() public document = <RegistryFormDto>{};
  public dto = DocumentCreateDto;
  public engine: 'class-transformer' | 'serde' = 'serde';

  @Input() public readonly index: number;
  @Input() public companies: CompanyFindInterface[] = [];
  @Input() public directions: DirectionFindInterface[] = [];
  @Input() public nds: NdsFindInterface[] = [];
  @Input() public bankDetails: BankDetailsFindInterface[] = [];

  @Input() public canCreateBasedOn = false;
  @Input() public canCreateCopyOn = false;
  @Input() public canCreateTemplate = false;
  @Input() public isModal = false;

  public loadingPdf = false;

  public currencies: CurrencyFindInterface[] = [];

  @Output() public bankDetailsSearch = new EventEmitter<string>();

  @Output() public createBasedOnClick = new EventEmitter<void>();

  private formSubscription$: Subscription = new Subscription();

  public get DocumentCalculationMethodEnum() {
    return DocumentCalculationMethodEnum;
  }

  public get DocumentKeyEnum() {
    return DocumentKeyEnum;
  }

  public constructor(
    private readonly documentRegistryService: DocumentRegistryService,
    public readonly globalService: GlobalService,
    private readonly formStoreService: FormStoreService,
    private readonly router: Router,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    if (!this.document.date) {
      this.document.date = moment().toDate();
    }
  }

  public ngOnDestroy(): void {
    this.formSubscription$?.unsubscribe();
  }

  public onSave(entity: RegistryFormDto) {
    if (this.checkValidate(this.document).valid) {
      const requiredAnalyticalFeatures = this.globalService.globalInfo?.requiredAnalyticalFeatures;
      if (
        (requiredAnalyticalFeatures?.directionsRequired && !this.document.direction) ||
        (requiredAnalyticalFeatures?.unitsRequired && !this.document.unit) ||
        (requiredAnalyticalFeatures?.filialsRequired && !this.document.filial)
      ) {
        this.errors = Object.create({});

        if (requiredAnalyticalFeatures?.directionsRequired && !this.document.direction) {
          this.errors.direction = 'Бюджетная статья не выбрана' as any;
        }
        if (requiredAnalyticalFeatures?.unitsRequired && !this.document.unit) {
          this.errors.unit = 'Бюджетная единица не выбрана' as any;
        }
        if (requiredAnalyticalFeatures?.filialsRequired && !this.document.filial) {
          this.errors.filial = 'Филиал не выбран' as any;
        }
        return;
      }

      if (this.document.documentType?.sync1c) {
        this.errors = null;
        this.save.emit(entity);
      } else {
        super.onSave(entity);
      }
    }
  }

  public changeValue() {
    if (this.document.nds?.amount !== null && this.document.nds?.amount !== undefined) {
      this.document.vatAmount = VatAmountCalculator.getInstance().getVatAmount(
        this.document.amount || 0,
        this.document.nds?.amount || 0
      );
    }
  }

  public createCopyDocument(data: { copyId: string }) {
    this.formStoreService.saveNext(this.document, this.index);

    const result = this.formStoreService.addForm(TypeFormEnum.DOCUMENT, {
      headerText: 'Создание документа',
      isNotShowRedirectInCreate: true,
      data
    });

    this.formSubscription$.add(
      result.onSave.subscribe(() => this.router.navigate(['/document/list']).catch(console.error))
    );
  }

  public createTemplate(data: { documentId: string }) {
    this.formStoreService.saveNext(this.document, this.index);

    const result = this.formStoreService.addForm(TypeFormEnum.DOCUMENT_TEMPLATE, {
      headerText: 'Новый шаблон',
      isNotShowRedirectInCreate: true,
      data
    });

    this.formSubscription$.add(
      result.onSave.subscribe(() => this.router.navigate(['/document/template']).catch(console.error))
    );
  }

  public isDocumentRelated(document: RegistryFormDto) {
    return (
      (document as DocumentFindInterface).groupCodes?.length &&
      (document as DocumentFindInterface).groupCodes.every((groupCode) => groupCode !== document?.code)
    );
  }

  public loadToPdf() {
    this.loadingPdf = true;
    this.cdRef.markForCheck();

    this.documentRegistryService.downloadOneAsPdf(this.document?.code).subscribe({
      next: (data) => {
        const file = new Blob([data], { type: 'application/pdf' });
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL, '_blank');
        this.loadingPdf = false;
        this.cdRef.markForCheck();
      },
      error: () => {
        this.loadingPdf = false;
        this.cdRef.markForCheck();
      }
    });
  }

  public changeCompany() {
    this.currencies = [];
    this.document.currency = null;

    if (this.document.company?.currencies?.length) {
      this.currencies = this.document.company.currencies;
      setTimeout(() => {
        this.document.currency = this.currencies.find((currency) => currency.isDefault);
        this.document['rate'] = this.document.currency?.latestRate?.rate;
        this.cdRef.markForCheck();
      });
    }
  }
}
